import { MediaType } from '../../types';
import type { IProvider, IAsset, SearchParams, AssetMetadata, ProviderCapabilities } from './types';

/**
 * Base provider class that all provider-specific implementations extend from.
 * Provides common HTTP client methods and structure.
 */
export abstract class BaseProvider implements IProvider {
  protected apiKey?: string;
  protected baseUrl: string;
  protected id: string;
  protected name: string;
  protected displayName: string;
  protected description: string;
  protected capabilities: ProviderCapabilities;

  constructor(config: {
    id: string;
    name: string;
    displayName: string;
    description: string;
    baseUrl: string;
    apiKey?: string;
    capabilities: ProviderCapabilities;
  }) {
    this.id = config.id;
    this.name = config.name;
    this.displayName = config.displayName;
    this.description = config.description;
    this.baseUrl = config.baseUrl;
    this.apiKey = config.apiKey;
    this.capabilities = config.capabilities;
  }

  // Metadata methods
  public getId(): string {
    return this.id;
  }

  public getName(): string {
    return this.name;
  }

  public getDisplayName(): string {
    return this.displayName;
  }

  public getDescription(): string {
    return this.description;
  }

  // Capability methods
  public canRead(): boolean {
    return this.capabilities.read;
  }

  public canWrite(): boolean {
    return this.capabilities.write;
  }

  public canDelete(): boolean {
    return this.capabilities.delete;
  }

  public getSupportedTypes(): MediaType[] {
    return this.capabilities.supportedTypes;
  }

  // Abstract methods that must be implemented by provider-specific classes
  public abstract search(params: SearchParams): Promise<IAsset[]>;
  public abstract getAssetById(id: string): Promise<IAsset>;

  // Optional methods for write operations
  public async upload?(file: File, metadata: AssetMetadata): Promise<IAsset>;
  public async delete?(assetId: string): Promise<void>;

  /**
   * Utility method to fetch JSON from an API
   * @param url - The URL to fetch from
   * @param options - Fetch options
   * @returns Promise with the parsed JSON response
   */
  protected async fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        ...options?.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return response.json();
  }
}
